// ============================================================================
// /people/hr/people/hourly — Partner-sourced workers.
//
// ⛔ This page used to have TWO tabs. The "W2 Hourly" tab is GONE (2026-08-25):
// it read `useW2HourlyEmployees`, which required `employment_type='W2'` and then
// excluded TCS email addresses client-side — the same heuristic that made
// non-W2 employees invisible everywhere. Every employee, at every pay type,
// now lives in the ONE directory at /people/hr/directory.
//
// What is left is NOT redundant with that directory and is why this file still
// exists: partner-sourced workers come from `service_assignments`, not from
// `employees`. They are people a subcontractor places at a facility — TCS has
// no employee row for them, so the employees directory cannot show them and
// deleting this page would have removed the only surface they appear on.
// ============================================================================
import { canWriteHrRecord } from '../../shared/role-gates';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  PageHeader,
  EmptyState,
  ErrorState,
  Skeleton,
  Card,
  Button,
  useToast,
} from '../../shared/components';
import { Briefcase, Pencil, Users } from 'lucide-react';
import { useAuth } from '../../shared/auth';
import { usePartnerWorkers, type PartnerWorkerRow } from './hooks/use-hr-data';
import WorkerEditModal from './worker-edit-modal';
import { fmtMonthDay } from './utils';


export default function HrPeopleHourly() {
  return (
    <div className="mx-auto max-w-6xl space-y-5 p-4 md:p-6">
      <PageHeader
        eyebrow="HR › People"
        title="Partner-sourced workers"
        meta="People a subcontractor places at a facility. They have no TCS employee record."
      />
      <div className="rounded-md border border-tcs-border bg-tcs-surface px-3 py-2 text-xs text-tcs-text-secondary">
        Looking for TCS employees? Every employee, salaried or hourly, is in the{' '}
        <Link to="/people/hr/directory" className="font-medium underline">
          <Users className="mb-0.5 inline h-3 w-3" /> Employees directory
        </Link>
        . This page is only for workers who come through a partner.
      </div>
      <PartnerTab />
      <div className="rounded-md border border-dashed border-tcs-border bg-tcs-surface px-4 py-3 text-xs text-tcs-text-muted">
        Hours panel coming soon — pending Buddy Punch ingest.
      </div>
    </div>
  );
}

function PartnerTab() {
  const { data, loading, error, refetch } = usePartnerWorkers();
  const { profile } = useAuth();
  const toast = useToast();
  const canWrite = canWriteHrRecord(profile);
  const [editing, setEditing] = useState<PartnerWorkerRow | null>(null);
  if (error) return <ErrorState title="Couldn't load partner workers" body={error} />;
  if (loading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-10" />
        ))}
      </div>
    );
  }
  if (data.length === 0) {
    return (
      <EmptyState
        icon={<Briefcase className="h-12 w-12" strokeWidth={1.5} />}
        title="No partner-sourced workers"
        body="service_assignments rows with worker_name will appear here."
      />
    );
  }
  return (
    <Card className="overflow-x-auto">
      <table className="min-w-full divide-y divide-tcs-border text-sm">
        <thead className="bg-tcs-surface text-left text-xs font-medium uppercase tracking-wide text-tcs-text-muted">
          <tr>
            <th className="px-3 py-2">Worker</th>
            <th className="px-3 py-2">Facility</th>
            <th className="px-3 py-2">What kind of work</th>
            <th className="px-3 py-2">Training</th>
            <th className="px-3 py-2">Training complete</th>
            <th className="px-3 py-2">Access issued</th>
            {canWrite && <th className="px-3 py-2">Actions</th>}
          </tr>
        </thead>
        <tbody className="divide-y divide-tcs-border">
          {data.map((r) => {
            // H4: lead with the PERSON. Company is the secondary line. When the
            // worker name hasn't been backfilled yet, show the company with a
            // "(name pending)" tag so the gap is visible rather than hidden.
            const company = r.provider_name ?? r.provider_id ?? 'Unknown company';
            const hasName = !!r.worker_name && r.worker_name.trim().length > 0;
            return (
              <tr key={r.assignment_id} className="hover:bg-tcs-surface">
                <td className="px-3 py-2">
                  {hasName ? (
                    <>
                      <div className="font-medium text-tcs-text">{r.worker_name}</div>
                      <div className="text-xs text-tcs-text-muted">{company}</div>
                    </>
                  ) : (
                    <>
                      <div className="font-medium text-tcs-text">
                        {company}{' '}
                        <span className="ml-1 rounded-full bg-st-warn-bg-strong px-1.5 py-0.5 text-[10px] font-medium text-st-warn-fg">
                          name pending
                        </span>
                      </div>
                      <div className="text-xs text-tcs-text-muted">Worker name not yet on file</div>
                    </>
                  )}
                </td>
                <td className="px-3 py-2 text-xs">{r.facility_name ?? r.facility_id ?? '—'}</td>
                <td className="px-3 py-2 text-xs">{r.service_label ?? '—'}</td>
                <td className="px-3 py-2 text-xs">{r.training_status ?? '—'}</td>
                <td className="px-3 py-2 text-xs">
                  {r.training_completion_date ? fmtMonthDay(r.training_completion_date) : '—'}
                </td>
                <td className="px-3 py-2 text-xs">{r.facility_access_issued ?? '—'}</td>
                {canWrite && (
                  <td className="px-3 py-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={<Pencil className="h-3.5 w-3.5" />}
                      onClick={() => setEditing(r)}
                    >
                      Edit
                    </Button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>

      {editing && (
        <WorkerEditModal
          worker={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            toast.success('Worker updated');
            refetch();
          }}
        />
      )}
    </Card>
  );
}
